import React, { createContext, useCallback, useContext, useEffect, useState } from "react"

const Ctx = createContext(null)

export function ChatProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false)
  const [username, setUsername] = useState(() => localStorage.getItem("chat_username") ?? "")
  const [pendingVaultLink, setPendingVaultLink] = useState(null)
  const [pendingVaultSelect, setPendingVaultSelect] = useState(null)
  const [windowHost, setWindowHost] = useState(null)
  const [windowVisible, setWindowVisible] = useState(false)
  // Unread is tracked per channel ("global" | "<username>" | "group:<id>") so
  // opening one chat only clears that chat's badge — others keep accumulating.
  const [unreadByChannel, setUnreadByChannel] = useState({})
  const [mentionCount, setMentionCount] = useState(0)
  const unreadCount = Object.values(unreadByChannel).reduce((a, b) => a + b, 0)

  const openChat = useCallback(() => setIsOpen(true), [])
  const closeChat = useCallback(() => setIsOpen(false), [])
  const toggleChat = useCallback(() => setIsOpen((v) => !v), [])
  const mountWindowedChat = useCallback((host) => {
    setIsOpen(false)
    setWindowHost(host)
    setWindowVisible(true)
    return () => {
      setIsOpen(false)
      setWindowHost((current) => current === host ? null : current)
      setWindowVisible(false)
    }
  }, [])
  const setWindowedChatVisible = useCallback((visible) => setWindowVisible(!!visible), [])

  const linkVaultNode = useCallback((path) => {
    setPendingVaultLink(path)
    setIsOpen(true)
  }, [])

  const clearVaultLink = useCallback(() => setPendingVaultLink(null), [])

  const selectVaultNode = useCallback((path) => setPendingVaultSelect(path), [])
  const clearVaultSelect = useCallback(() => setPendingVaultSelect(null), [])

  const saveUsername = useCallback((name) => {
    setUsername(name)
    localStorage.setItem("chat_username", name)
  }, [])

  const addUnread = useCallback((channel, n = 1) => {
    if (!channel) return
    setUnreadByChannel((m) => ({ ...m, [channel]: (m[channel] ?? 0) + n }))
  }, [])
  // clearUnread(channel) clears one chat; clearUnread() with no arg clears all.
  const clearUnread = useCallback((channel) => {
    if (channel == null) { setUnreadByChannel({}); return }
    setUnreadByChannel((m) => {
      if (!m[channel]) return m
      const next = { ...m }; delete next[channel]; return next
    })
  }, [])

  const addMention = useCallback((n = 1) => setMentionCount((c) => c + n), [])
  const clearMention = useCallback(() => setMentionCount(0), [])

  // Browser tab title: show unread count when chat is closed
  useEffect(() => {
    document.title = unreadCount > 0 && !isOpen && (!windowHost || !windowVisible) ? `(${unreadCount}) HOLOS` : "HOLOS"
  }, [unreadCount, isOpen, windowHost, windowVisible])

  return (
    <Ctx.Provider value={{ isOpen, setIsOpen, openChat, closeChat, toggleChat, chatWindowHost: windowHost, chatWindowVisible: windowVisible, mountWindowedChat, setWindowedChatVisible, username, saveUsername, pendingVaultLink, linkVaultNode, clearVaultLink, pendingVaultSelect, selectVaultNode, clearVaultSelect, unreadCount, addUnread, clearUnread, mentionCount, addMention, clearMention }}>
      {children}
    </Ctx.Provider>
  )
}

export function useChatContext() {
  return useContext(Ctx)
}
